import {
  ChevronRightIcon,
  MenuIcon,
  X,
  ChevronDownIcon,
  EarthIcon,
  Building2Icon,
  StoreIcon,
  StarIcon,
  Ban,
} from "lucide-react";
import React, { useEffect, useState } from "react";
import {
  category_one,
  category_two,
  category_three,
  signup_url,
} from "../api/urls";
import useFetch from "../hooks/useFetch";
import BodyPortal from "./BodyPortal";
import { Link, useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import { Box, Button, Modal, Typography } from "@mui/material";

const CategorySelection = ({ handleSignin }) => {
  const [open, setOpen] = useState(false);
  const [openSecondID, setOpenSecondID] = useState(null);
  const [firstID, setFirstID] = useState(null);

  /* --------------------
     API calls
  -------------------- */

  const { accessToken } = useSelector((state) => state.auth);
  const { data: userData } = useFetch(accessToken && signup_url);
  const { data: catOneData, loading: catOneLoading } = useFetch(category_one);
  const [b2bErrorPopup, setB2bErrorPopup] = useState(false);

  const secondApi = firstID && `${category_two}?category_first=${firstID}`;
  const { data: catTwoData, loading } = useFetch(secondApi);

  const thirdApi =
    openSecondID && `${category_three}?category_second=${openSecondID}`;
  const { data: rawCatThreeData, loading: catThreeLoading } =
    useFetch(thirdApi);
  const [catThreeData, setCatThreeData] = useState([]);
  useEffect(() => {
    if (rawCatThreeData?.length) {
      setCatThreeData(rawCatThreeData);
    } else {
      setCatThreeData([]);
    }
  }, [rawCatThreeData]);

  useEffect(() => {
    setOpenSecondID(null);
    setCatThreeData([]);
  }, [firstID]);

  useEffect(() => {
    if (catOneData?.length && !firstID) {
      setFirstID(catOneData[0].id);
    }
  }, [catOneData, firstID]);

  useEffect(() => {
    if (catTwoData?.length) {
      setOpenSecondID(catTwoData[0].id);
    }
  }, [catTwoData]);

  useEffect(() => {
    if (!catTwoData || catTwoData.length === 0) {
      setOpenSecondID(null);
      setCatThreeData([]); // 🧹 no second cats = no third cats
    }
  }, [catTwoData]);

  const navigate = useNavigate();
  return (
    <>
      {/* Top bar */}
      <section className="border-t border-b p-2 px-6 flex gap-10 text-slate-800">
        <button
          onClick={() => setOpen(true)}
          className="flex items-center gap-2 font-medium"
        >
          <MenuIcon size={20} />
          All Categories
        </button>
        <Link
          to={"/global_store"}
          className="flex items-center gap-2 font-medium"
        >
          <EarthIcon size={20} />
          Global Store
        </Link>
        <p
          // to={"/seller"}
          className="flex items-center gap-2 font-medium cursor-pointer"
          onClick={() => {
            if (!accessToken) {
              handleSignin();
            } else {
              navigate("/seller");
            }
          }}
        >
          <StoreIcon size={20} />
          Sellers
        </p>
        <Link to={"/brand"} className="flex items-center gap-2 font-medium">
          <StarIcon size={20} />
          Brands
        </Link>
        <button
          onClick={() => {
            if (userData?.bnpl_approved) {
              navigate("/b2b_store");
            } else {
              setB2bErrorPopup(true);
            }
          }}
          className="flex items-center gap-2 font-medium"
        >
          <Building2Icon size={20} />
          B2B Store
        </button>

        <B2BErrorModal
          b2bErrorPopup={b2bErrorPopup}
          setB2bErrorPopup={setB2bErrorPopup}
        />
      </section>

      {open && (
        <BodyPortal>
          <div className="fixed inset-0 z-[9999] bg-black/30">
            <div className="absolute top-28 left-5 bg-white rounded-xl shadow-xl xl:w-[60vw]  w-[90vw] mx-auto">
              {/* Header */}
              <div className="flex justify-between items-center p-5 bg-gray-100 rounded-t-xl">
                <h3 className="font-semibold">All Categories</h3>
                <X
                  className="cursor-pointer"
                  onClick={() => {
                    setOpen(false);
                    setFirstID(null);
                    setOpenSecondID(null);
                  }}
                />
              </div>

              {/* BODY */}
              <div className="flex ">
                {/* LEFT COLUMN */}
                <div className=" border-r p-4 2xl:h-[75vh] h-[50vh] overflow-y-auto">
                  {/* First categories */}
                  <div className="space-y-1">
                    {catOneLoading ? (
                      <div className="space-y-4">
                        {Array.from({ length: 13 }).map((_, i) => (
                          <div
                            key={i}
                            className="h-8 p-4 w-[250px] bg-gray-300 animate-pulse rounded"
                          />
                        ))}
                      </div>
                    ) : (
                      catOneData?.map((first) => (
                        <div>
                          <button
                            key={first.id}
                            onClick={() => {
                              setFirstID(
                                first.id === firstID ? null : first.id,
                              );
                              setOpenSecondID(null);
                            }}
                            className={`w-full flex justify-between items-center truncate px-3 py-2 rounded-md text-left hover:bg-gray-100 ${
                              firstID === first.id
                                ? "bg-gray-100 font-medium"
                                : ""
                            }`}
                          >
                            {first.printable_name}
                            {firstID === first.id ? (
                              <ChevronDownIcon size={16} />
                            ) : (
                              <ChevronRightIcon size={16} />
                            )}
                          </button>
                          {/* Second categories (Accordion) */}
                          {firstID === first.id && (
                            <>
                              {loading ? (
                                <div className="flex flex-col gap-4 p-4">
                                  {Array.from({ length: 2 }).map((_, i) => (
                                    <div
                                      key={i}
                                      className="h-8 p-4 w-full bg-gray-300 animate-pulse rounded"
                                    />
                                  ))}
                                </div>
                              ) : catTwoData?.length > 0 ? (
                                <div className="space-y-2 p-2">
                                  {catTwoData.map((second) => {
                                    const isOpen = openSecondID === second.id;

                                    return (
                                      <button
                                        key={second.id}
                                        onClick={() =>
                                          setOpenSecondID(
                                            isOpen ? null : second.id,
                                          )
                                        }
                                        className={`w-full flex justify-between items-center px-3 py-2 rounded-md text-left hover:bg-gray-100 text-gray-600 text-sm ${
                                          isOpen
                                            ? "bg-gray-100 font-medium"
                                            : ""
                                        }`}
                                      >
                                        {second.printable_name}
                                      </button>
                                    );
                                  })}
                                </div>
                              ) : (
                                <div className="p-4 text-gray-400">
                                  No Categories
                                </div>
                              )}
                            </>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                </div>

                {/* RIGHT COLUMN – THIRD CATEGORIES ONLY */}
                <div className="flex-1 p-6 2xl:h-[75vh] h-[50vh] overflow-y-auto">
                  {/* {!openSecondID && (
                    <p className="text-sm text-gray-400">
                      Select a subcategory to view items
                    </p>
                  )} */}

                  <div className="grid lg:grid-cols-2 gap-6">
                    {catThreeLoading
                      ? Array.from({ length: 2 }).map((_, i) => (
                          <div
                            key={i}
                            className="h-[80px] p-4 flex-1 bg-gray-300 animate-pulse rounded"
                          />
                        ))
                      : catThreeData?.map((third) => (
                          <div
                            key={third.id}
                            onClick={() => {
                              navigate(`/products?third_category=${third.id}`);
                              setOpen(false);
                            }}
                            className="flex items-center gap-3 p-3 rounded-lg bg-gray-50 hover:bg-gray-100 cursor-pointer"
                          >
                            <img
                              src={third.cat03_image}
                              alt={third.printable_name}
                              className="w-12 h-12 rounded-md object-cover"
                            />
                            <div>
                              <p className="font-medium">
                                {third.printable_name}
                              </p>
                            </div>
                          </div>
                        ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </BodyPortal>
      )}
    </>
  );
};

export default CategorySelection;

const B2BErrorModal = ({ b2bErrorPopup, setB2bErrorPopup }) => {
  return (
    <Modal open={b2bErrorPopup} onClose={() => setB2bErrorPopup(false)}>
      <Box
        sx={{
          position: "absolute",
          top: "50%",
          left: "50%",
          transform: "translate(-50%, -50%)",
          backgroundColor: "#fff",
          p: 10,
          borderRadius: "40px",
          textAlign: "center",
          width: "470px",
          boxShadow: "0 4px 10px rgba(0,0,0,0.1)",
        }}
      >
        {/* Red circle with slash icon */}
        {/* <Ban
      style={{
        color: "red",
        fontSize: "70px",
        marginBottom: "12px",
      }}
    /> */}
        <div className="flex justify-center items-center mb-4">
          <Ban style={{ color: "#dd2638", width: 60, height: 60 }} />
        </div>

        {/* Main heading */}
        <Typography
          variant="h6"
          sx={{
            fontWeight: 700,
            fontSize: "20px",
            mb: 1,
          }}
        >
          You are not eligible
          <br />
          to access B2B Store.
        </Typography>

        {/* Description text */}
        <Typography
          variant="body2"
          sx={{
            color: "#555",
            fontSize: "13px",
            mb: 3,
            lineHeight: 1.4,
          }}
        >
          to Get the Access of B2B Store Please Upload the Documents from our
          Pentagon E-store Application :
        </Typography>

        {/* Button */}
        {/* <Button
      variant="contained"
      sx={{
        backgroundColor: "#000",
        textTransform: "none",
        borderRadius: "6px",
        fontSize: "13px",
        fontWeight: 500,
        px: 3,
        "&:hover": { backgroundColor: "#333" },
      }}
      onClick={() => setB2bErrorPopup(false)}
    >
      Download App
    </Button> */}
        <Button
          variant="contained"
          sx={{
            backgroundColor: "#000",
            textTransform: "none",
            borderRadius: "6px",
            fontSize: "13px",

            px: 13,
            "&:hover": { backgroundColor: "#333" },
          }}
          onClick={() => {
            window.open(
              "https://play.google.com/store/apps/details?id=com.tagonstore.shop",
              "_blank",
            );
            setB2bErrorPopup(false);
          }}
        >
          Download App
        </Button>
      </Box>
    </Modal>
  );
};
